import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { AppConfigService } from 'app/core/config/app-config.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Observable, ReplaySubject, tap, take } from 'rxjs';
import { AdminNavigationService } from './admin-navigation.service';
import { AuthRepository, UserRole } from '../auth/auth.repository';
import { CompaniesService } from 'app/services/companies.service';
import { FeatureSettingsService } from 'app/services/feature-settings.service';

/** Maps nav item id → feature flag that must be enabled to show the item. */
const NAV_FEATURE_GATES: Record<string, string> = {
  // Top-level items
  'supervisor-classroom':      'feature_classroom',
  'reports':                   'feature_reports',
  // Settings children
  'settings.vclass-admin':     'feature_classroom',
  // Course Admin children
  'course-admin.badges':       'feature_badges',
  'course-admin.study-matrix': 'feature_study_matrix',
  'course-admin.groups':       'feature_student_groups',
};

/**
 * Nav items that are only visible to SuperUser / SysAdmin.
 * CourseAdmin, Supervisor, Advisor, and Translator must not see these.
 */
const NAV_SUPERADMIN_ONLY = new Set([
  'settings.companies',        // "Companies" list
  'settings.translators',
  'settings.permissions',
  'settings.system-settings',  // "Company Settings" link
  'settings.system',           // "System" link
]);

@Injectable({providedIn: 'root'})
export class NavigationService
{
  readonly appConfig = inject(AppConfigService);
    private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _adminNavigationService: AdminNavigationService,
        private _authRepository: AuthRepository,
        private _companiesService: CompaniesService,
        private _featureSettingsService: FeatureSettingsService
    )
    {
    }

    /**
     * Whether the Website Sales feature is enabled for the user's company.
     * SuperUser/SysAdmin always qualify so they can reach sales across companies.
     */
    private _isWebsiteSalesEnabled(): boolean {
        if (this._adminNavigationService.isSuperOrSysAdmin()) {
            return true;
        }
        return !!this._featureSettingsService.getCompanySettings()?.website_sales?.enabled;
    }

    /**
     * Whether the current user may edit courses (and therefore see/use the legacy
     * "Course Creation" item). SuperUser/SysAdmin/CourseAdmin always qualify; any other
     * role (e.g. Supervisor) needs the `do_edit_course` permission. Students never qualify.
     */
    private _canEditCourses(): boolean {
        const user = this._authRepository.getUser();
        if (!user) return false;
        if (user.role === UserRole.SuperUser || user.role === UserRole.SysAdmin || user.role === UserRole.CourseAdmin) {
            return true;
        }
        return user.permissions?.includes('do_edit_course') ?? false;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation>
    {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation>
    {
        return this._httpClient.get<Navigation>('api/common/navigation').pipe(
            tap((navigation) =>
            {
                // Update admin navigation visibility based on user role
                if (this._adminNavigationService.hasAdminRole()) {
                    // Ensure admin navigation is available
                    if (!navigation.admin) {
                        navigation.admin = [];
                    }
                } else {
                    // Clear admin navigation for non-admin users
                    navigation.admin = [];
                }

                // Remove nav items (and their children) whose feature flag is disabled,
                // or whose role requirement is not met.
                // Runs recursively so child items (e.g. Course Admin sub-items) are also filtered.
                const isSuperAdmin = this._adminNavigationService.isSuperOrSysAdmin();
                const filter = (items: FuseNavigationItem[]): FuseNavigationItem[] =>
                    items
                        .filter(item => {
                            const featureKey = NAV_FEATURE_GATES[item.id];
                            if (featureKey !== undefined && !this._featureSettingsService.isFeatureEnabled(featureKey)) return false;
                            if (NAV_SUPERADMIN_ONLY.has(item.id) && !isSuperAdmin) return false;
                            // "Course Creation" (legacy editor) requires course-edit rights.
                            if (item.id === 'course-admin.legacy-creation' && !this._canEditCourses()) return false;
                            // "Sales" requires the per-company Website Sales feature to be enabled.
                            if (item.id === 'sales' && !this._isWebsiteSalesEnabled()) return false;
                            return true;
                        })
                        .map(item => item.children?.length
                            ? { ...item, children: filter(item.children) }
                            : item
                        );

                if (navigation.compact)     { navigation.compact     = filter(navigation.compact); }
                if (navigation.admin)       { navigation.admin       = filter(navigation.admin); }
                if (navigation.default)     { navigation.default     = filter(navigation.default); }
                if (navigation.futuristic)  { navigation.futuristic  = filter(navigation.futuristic); }
                if (navigation.horizontal)  { navigation.horizontal  = filter(navigation.horizontal); }

                // Wire the "Course Creation" item to the legacy-system SSO auto-login.
                // The mock-api defines it without a link/function, so it would otherwise be inert.
                const wireLegacyAction = (items: FuseNavigationItem[]): void => {
                    for (const item of items) {
                        if (item.id === 'course-admin.legacy-creation') {
                            item.function = () => this.openLegacyCourseEditor();
                        }
                        if (item.children?.length) { wireLegacyAction(item.children); }
                    }
                };
                if (navigation.compact)     { wireLegacyAction(navigation.compact); }
                if (navigation.admin)       { wireLegacyAction(navigation.admin); }
                if (navigation.default)     { wireLegacyAction(navigation.default); }
                if (navigation.futuristic)  { wireLegacyAction(navigation.futuristic); }
                if (navigation.horizontal)  { wireLegacyAction(navigation.horizontal); }

                this._navigation.next(navigation);
            }),
        );
    }

    /**
     * Open the legacy course editor with an SSO auto-login.
     *
     * When a company is in scope, log in AS that company's course-admin account by
     * resolving its impersonation triple (uid/fid/role) from the backend and POSTing it
     * to the legacy `belogin.php`. If no company is selected or the lookup fails, fall
     * back to the current user's own credentials so global admins still get in. Either
     * way a `redirect` lands the user directly on the course editor.
     */
    private openLegacyCourseEditor(): void {
        const companyId = this._companiesService.getEffectiveCompanyId();

        if (companyId) {
            this._companiesService.getLegacyCourseAdminSso(companyId)
                .pipe(take(1))
                .subscribe(account => {
                    if (account) {
                        this._submitLegacyForm(account.uid, account.fid, account.role);
                    } else {
                        this._submitLegacyFormAsCurrentUser();
                    }
                });
            return;
        }

        this._submitLegacyFormAsCurrentUser();
    }

    /** SSO into the legacy course editor using the current user's own credentials. */
    private _submitLegacyFormAsCurrentUser(): void {
        const user = this._authRepository.getUser();
        this._submitLegacyForm(
            Number(user?.user_id ?? 0),
            Number(user?.person_id ?? 0),
            Number(user?.role ?? 0)
        );
    }

    /**
     * Build and submit a hidden form POSTing the given impersonation triple to the legacy
     * `belogin.php`, redirecting to the course editor in a new tab. The legacy base URL
     * comes from the `otrainingsystem` setting.
     */
    private _submitLegacyForm(uid: number, fid: number, role: number): void {
        const base  = String(this.appConfig.get('otrainingsystem') || '').replace(/\/+$/, '');
        const token = this._authRepository.getAccessToken();

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = `${base}/belogin.php`;
        form.target = '_blank';
        form.style.display = 'none';

        const addField = (name: string, value: string): void => {
            const input = document.createElement('input');
            input.type  = 'hidden';
            input.name  = name;
            input.value = value;
            form.appendChild(input);
        };

        addField('Authorization', token ?? '');
        addField('uid', '' + uid);
        addField('fid', '' + fid);
        addField('role', '' + role);
        addField('redirect', 'course-editor.php');

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
    }
}
